import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import moment from 'moment-timezone';
import {find, isEmpty} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';

import {setCurrentModal, setUserReportOrderDetails} from '~/shared/store/actions';
import {isAllowedToStepBackward} from '~/shared/components/UserTransactions/utils';
import {handleRefreshToken} from '~/shared/services/auth';
import {setIn} from '~/shared/utils/general';
import {getCurrentLocation, pushRoute, replaceRoute} from '~/shared/router';
import {selectUserData, selectUserLoaded} from '~/shared/store/selectors';
import {getLocalizationService} from '~/shared/services/localisationService';
import apiService from '~/shared/services/apiService';
import {EMPTY_ARRAY, EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {trackEvent} from '~/shared/services/analytics';
import {ApiError, is401Error} from '~/shared/services/apiErrorService';
import {createLogger} from '~/shared/logging';
import {
  UserReportApiResponse,
  Payment,
  DebitTransfer,
  Moneycard,
} from '~/shared/services/apiService/models';
import {mapDebitTransferStatus} from '~/shared/consts/userTransactionsConsts';
import {ActiveOrderState} from '~/shared/store/models';
import {PersonalAreaTabs} from '~/shared/consts/personalArea';
import {sections} from '~/common/personalAreaSections';

import {useTransactionsRangeRedirect} from './useTransactionsRangeRedirect';

interface UseUserReportDataParams {
  dateBias: string;
  baseUrl: string;
  additionalQuery?: string;
}

interface TransferStatusChangeParams {
  transferId: number;
  newStatusForRequest: string;
  transferType: 'debitTransfersFromUser' | 'debitTransfersToUser';
  onSuccess?: (data: Record<string, UserReportApiResponse>) => void;
  onError?: (data: Record<string, UserReportApiResponse>) => void;
}

/**
 * Custom hook to manage all data operations for the UserReport component.
 * Handles API calls, data transformations, transfer status management, and side effects.
 *
 * @param dateBias - The date bias indicating the report period (0 = current, -1 = previous month, etc.)
 * @returns Object containing all data, computed values, and handler functions
 */

const logger = createLogger('useUserReportData');

export const useUserReportData = ({dateBias, baseUrl, additionalQuery}: UseUserReportDataParams) => {
  const {currentLanguageKey} = getLocalizationService();
  const userLoaded = useSelector(selectUserLoaded);
  const userData = useSelector(selectUserData);
  const [creditCardsListById, setCreditCardsListById] = useState<Record<string, boolean>>({});
  const dispatch = useDispatch();
  const {query} = getCurrentLocation();
  const [data, setData] = useState<Record<string, UserReportApiResponse>>(EMPTY_OBJECT);
  const [filter, setFilter] = useState('');

  const isCompanyUser = Boolean(userData?.isCompanyUser);

  const lastDateBiasInUserTransactionReport = useRef('');

  /**
   * Builds the URL with query parameters
   */
  const buildUrlWithQuery = useCallback(
    (newDateBias: number) => {
      const queryParams = [];
      if (additionalQuery) {
        queryParams.push(additionalQuery);
      }
      queryParams.push(`dateBias=${newDateBias}`);
      return `${baseUrl}?${queryParams.join('&')}`;
    },
    [baseUrl, additionalQuery],
  );

  const numberDateBias = Number(dateBias);
  const currentDateBiasValue = data[numberDateBias];
  const transferRequestsReceived = currentDateBiasValue?.debitTransfers?.debitTransfersToUser || EMPTY_ARRAY;

  useTransactionsRangeRedirect({
    dateBias: numberDateBias,
    currentDateBiasValue,
    buildUrlWithQuery,
  });

  const checkIfMoneycardIsCredit = useCallback(
    (moneycardId: number) => Boolean(creditCardsListById[moneycardId]),
    [creditCardsListById],
  );

  const isIntelReport = currentDateBiasValue?.reportSetting?.isIntelReport;
  const isKlaReport = currentDateBiasValue?.reportSetting?.isKlaReport;
  const isGroupByDayReport = currentDateBiasValue?.reportSetting?.isGroupByDay;
  const userActiveOrders = currentDateBiasValue?.userActiveOrders?.length ? currentDateBiasValue.userActiveOrders : [];
  const allowedToStepForward = useMemo(() => Number(dateBias) < 0, [dateBias]);
  const showNoOrdersThisMonth = (currentDateBiasValue?.orderList?.length === 0 && !(isCompanyUser && currentDateBiasValue.dateBias === 0));

  const allowedToStepBackward = useMemo(
    () =>
      isAllowedToStepBackward(
        currentDateBiasValue?.userCreated,
        currentDateBiasValue?.companyReportRange?.startDateStr,
      ),
    [currentDateBiasValue],
  );

  /**
   * Combines payments with the same payment method and card suffix
   */
  const combineSamePayments = useCallback((payments: Payment[]) => {
    const combinedPayments = payments?.reduce<Payment[]>((allPayments, payment) => {
      /**
       * summing the totals of the same payments (same = has the same paymentMethod and cardSuffix)
       * as same card suffix may appear multiple times with different paymentId and different total.
       */
      const handledPayment = find(allPayments, {
        paymentMethod: payment.paymentMethod,
        cardSuffix: payment.cardSuffix,
      });
      if (!handledPayment) {
        return [...allPayments, payment];
      }

      handledPayment.total += payment.total;
      return allPayments;
    }, []);

    return combinedPayments;
  }, []);

  /**
   * Fetches user transaction report data for the given date bias
   */
  const setOrderListData = useCallback(async () => {
    if (!dateBias) {
      return;
    }
    // Last date bias should be tracked before request
    lastDateBiasInUserTransactionReport.current = dateBias;
    try {
      const {data: newData} = (await apiService.userTransactionsReport({dateBias})) as {
        data: UserReportApiResponse;
      };

      if (!newData) {
        return;
      }

      setCreditCardsListById(newData.moneycards.reduce((all: Record<string, boolean>, item: Moneycard) => {
        if (item.isTenbisCredit) {
          return {
            ...all,
            [item.moneycardId]: true,
          };
        }

        return {...all, [item.moneycardId]: false};
      }, {}));

      const newDataWithCombinedPayments = setIn(
        newData,
        ['totals', 'payments'],
        combineSamePayments(newData?.totals?.payments),
      );

      const newOrderList = newDataWithCombinedPayments.orderList.map(orderItem => ({
        ...orderItem,
        isCanceled: orderItem.orderStatus === 4,
        review: {...orderItem.review, reviewRank: orderItem.review.reviewRank / 2},
      }));

      const newDataWithCombinedPaymentsWithNewOrderList = setIn(
        newDataWithCombinedPayments,
        ['orderList'],
        newOrderList,
      );

      setData({
        ...data,
        [dateBias]: newDataWithCombinedPaymentsWithNewOrderList,
      });
    } catch (e) {
      if (is401Error(e)) {
        await handleRefreshToken(e, setOrderListData);
        return;
      }

      throw e;
    }
  }, [combineSamePayments, data, dateBias]);

  const changeTransferStatus = useCallback(
    async ({
      transferId,
      newStatusForRequest: status,
      transferType,
      onSuccess: _onSuccess,
      onError: _onError,
    }: TransferStatusChangeParams) => {
      const dataWithNewRequestStatus = (newTransferStatus: string) =>
        setIn(
          data,
          [dateBias, 'debitTransfers', transferType],
          data[dateBias].debitTransfers[transferType].map((transfer: DebitTransfer) => ({
            ...transfer,
            debitTransferRequestStatus:
              transfer.transferId === transferId ? newTransferStatus : transfer.debitTransferRequestStatus,
          })),
        );

      const updateDebitTransferRequestStatus = async () => {
        const {data: resData} = await apiService.setTransferDebitRequestStatus({
          status,
          transferId,
        });

        const {debitTransferRequestStatus: updatedDebitTransferRequestStatus} =
          resData;
        const updatedData = dataWithNewRequestStatus(updatedDebitTransferRequestStatus);
        setData(updatedData);
        return updatedData[dateBias].debitTransfers.debitTransfersToUser;
      };

      try {
        return updateDebitTransferRequestStatus();
      } catch (error) {
        if (is401Error(error)) {
          await handleRefreshToken(error, updateDebitTransferRequestStatus);
          return;
        }
        logger.warn('debit transfer status update failed', (error as ApiError)?.message);
        setData(dataWithNewRequestStatus(mapDebitTransferStatus.Failed));
      }
    },
    [data, dateBias],
  );

  /**
   * Opens modal for debit transfer requests if there are pending validated transfers
   */
  const openDebitTransferRequestsReceivedModalIfNeeded = useCallback(() => {
    const filtered = transferRequestsReceived?.filter(
      (transfer: DebitTransfer) => transfer.debitTransferRequestStatus.toLowerCase() === 'validated',
    );
    if (!currentDateBiasValue || (filtered || transferRequestsReceived).length === 0) {
      return;
    }
    const transferAction = ({transferId, isApproved}: {transferId: number; isApproved: boolean}) =>
      changeTransferStatus({
        transferType: 'debitTransfersToUser',
        transferId,
        newStatusForRequest: isApproved ? mapDebitTransferStatus.Approved : mapDebitTransferStatus.Declined,
        onSuccess: dataWithNewRequestStatus => {
          setData(dataWithNewRequestStatus);
        },
        onError: dataWithNewRequestStatus => setData(dataWithNewRequestStatus),
      });

    dispatch(
      setCurrentModal('debitTransferRequestsReceivedModal', {
        transfers: transferRequestsReceived,
        action: transferAction,
        status: mapDebitTransferStatus,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferRequestsReceived, dispatch]);

  /**
   * Navigate to previous month's report
   */
  const stepBackward = useCallback(() => {
    if (!allowedToStepBackward) {
      return;
    }

    setFilter('');
    const dateSelected = moment(currentDateBiasValue?.companyReportRange?.startDateStr, 'DD/MM/YYYY')
      .locale(currentLanguageKey)
      .subtract(1, 'month')
      .format('YYYY-MM');

    trackEvent('hasChangedDateInBillingReport', {dateSelected});

    const newDateBias = Number(dateBias) - 1;
    pushRoute(buildUrlWithQuery(newDateBias));
  }, [allowedToStepBackward, currentDateBiasValue?.companyReportRange?.startDateStr, currentLanguageKey, dateBias, buildUrlWithQuery]);

  /**
   * Navigate to next month's report
   */
  const stepForward = useCallback(() => {
    if (!allowedToStepForward) {
      return;
    }
    setFilter('');
    const dateSelected = moment(currentDateBiasValue?.companyReportRange?.startDateStr, 'DD/MM/YYYY')
      .locale(currentLanguageKey)
      .add(1, 'month')
      .format('YYYY-MM');

    trackEvent('hasChangedDateInBillingReport', {dateSelected});

    const newDateBias = Number(dateBias) + 1;
    pushRoute(buildUrlWithQuery(newDateBias));
  }, [allowedToStepForward, currentDateBiasValue?.companyReportRange?.startDateStr, currentLanguageKey, dateBias, buildUrlWithQuery]);

  /**
   * Cancels a sent debit transfer
   */
  const onSentDebitTransferCancelClick = useCallback(
    (transferId: number) => {
      changeTransferStatus({
        transferType: 'debitTransfersFromUser',
        transferId,
        newStatusForRequest: mapDebitTransferStatus.Cancelled,
        onSuccess: dataWithNewRequestStatus => setData(dataWithNewRequestStatus),
        onError: dataWithNewRequestStatus => setData(dataWithNewRequestStatus),
      });
    },
    [changeTransferStatus],
  );

  const onMobileTransactionClick = useCallback(
    async (transaction: ActiveOrderState) => {
      await dispatch(setUserReportOrderDetails(transaction));
      pushRoute(`${baseUrl}?showOrder=true`);
    },
    [baseUrl, dispatch],
  );

  // Effect: Open debit transfer requests modal when needed
  useEffect(() => {
    openDebitTransferRequestsReceivedModalIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferRequestsReceived]);

  // Effect: Fetch data when dateBias changes
  const {section} = query;
  useEffect(() => {
    if (lastDateBiasInUserTransactionReport.current === dateBias) {
      return;
    }

    const dateBiasDoesntExistOnQuery = (() => {
      const hasDateBias = isEmpty(dateBias) || Number(dateBias).toString() === 'NaN';
      if (additionalQuery) {
        return section === sections[PersonalAreaTabs.MY_ORDERS].query && hasDateBias;
      }

      return hasDateBias;
    })();
    
    if (dateBiasDoesntExistOnQuery) {
      replaceRoute(buildUrlWithQuery(0), {keepAllQueries: false});
    }

    if (!userLoaded) {
      return;
    }

    const asyncSetOrderListData = async () => {
      await setOrderListData();
    };

    asyncSetOrderListData();
  }, [userLoaded, dateBias, userData, setOrderListData, buildUrlWithQuery, section, additionalQuery]);

  return {
    // Core data
    data,
    setData,
    currentDateBiasValue,
    numberDateBias,

    // Derived data
    transferRequestsReceived,
    userActiveOrders,

    // Report type flags
    isIntelReport,
    isKlaReport,
    isGroupByDayReport,
    isCompanyUser,
    userLoaded,

    // Navigation
    allowedToStepForward,
    allowedToStepBackward,
    stepBackward,
    stepForward,

    // Filter
    filter,
    setFilter,

    // Handlers
    setOrderListData,
    changeTransferStatus,
    onSentDebitTransferCancelClick,
    onMobileTransactionClick,

    // Utilities
    checkIfMoneycardIsCredit,
    showNoOrdersThisMonth,
  };
};
